/**
 * ThemeToggle.jsx
 *
 * Compact theme-swatcher for Crown & Rose — The Loom.
 * Shows a small coloured dot representing the active theme; clicking opens a
 * popover with five named swatches: Light, Graphite, Midnight, Nord, Solarized.
 */
import React, { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { applyThemeToDOM, VALID_THEMES } from '@/lib/store/useTheme';

/* ── Theme registry ──────────────────────────────────────────────────────── */
export const THEMES = [
  {
    id:    'light',
    label: 'Light',
    desc:  'Warm parchment',
    bg:    '#f5f0ea',
    accent:'#7a1c23',
    text:  '#3b2a2e',
  },
  {
    id:    'graphite',
    label: 'Graphite',
    desc:  'Warm charcoal',
    bg:    '#2b2826',
    accent:'#a03038',
    text:  '#e5ddd5',
  },
  {
    id:    'midnight',
    label: 'Midnight',
    desc:  'Deep studio black',
    bg:    '#121113',
    accent:'#a03038',
    text:  '#e6e1e3',
  },
  {
    id:    'nord',
    label: 'Nord',
    desc:  'Cool blue-grey',
    bg:    '#2e3440',
    accent:'#88c0d0',
    text:  '#d8dee9',
  },
  {
    id:    'solarized',
    label: 'Solarized',
    desc:  'Warm amber-teal',
    bg:    '#002b36',
    accent:'#b58900',
    text:  '#fdf6e3',
  },
  {
    id:    'system',
    label: 'System',
    desc:  'Follow your OS',
    bg:    'linear-gradient(135deg, #f5f0ea 50%, #2b2826 50%)',
    accent:'#888',
    text:  '#888',
    isGradient: true,
  },
];

function readStored() {
  try {
    const v = localStorage.getItem('pw-theme') || 'system';
    return v === 'dark' ? 'graphite' : v;
  } catch { return 'system'; }
}

/** Compact swatcher button + popover. */
export function ThemeToggle({ className }) {
  const [active, setActive]   = useState(readStored);
  const [open,   setOpen]     = useState(false);

  // Sync when another tab / the server changes the theme
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'pw-theme' && e.newValue) {
        const v = e.newValue === 'dark' ? 'graphite' : e.newValue;
        if (VALID_THEMES.includes(v)) setActive(v);
      }
    };
    const onSync = (e) => {
      const v = e.detail === 'dark' ? 'graphite' : e.detail;
      if (v && VALID_THEMES.includes(v)) setActive(v);
    };
    window.addEventListener('storage',        onStorage);
    window.addEventListener('pw:theme-synced', onSync);
    return () => {
      window.removeEventListener('storage',        onStorage);
      window.removeEventListener('pw:theme-synced', onSync);
    };
  }, []);

  const pick = (id) => {
    setActive(id);
    setOpen(false);
    localStorage.setItem('pw-theme', id);
    const resolved = id === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'graphite' : 'light')
      : id;
    applyThemeToDOM(resolved);
    // Let useTheme.js pick up the localStorage change on next render cycle
    window.dispatchEvent(new StorageEvent('storage', { key: 'pw-theme', newValue: id }));
  };

  const current = THEMES.find((t) => t.id === active) ?? THEMES[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="theme-toggle-btn"
          aria-label={`Theme: ${current.label}. Click to change`}
          title={`Theme: ${current.label}`}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md',
            'text-muted-foreground hover:text-foreground hover:bg-muted/60',
            'transition-colors duration-150',
            className,
          )}
        >
          {/* Mini dot preview of the active theme */}
          <span className="flex items-center gap-0.5">
            <span
              className="h-2.5 w-2.5 rounded-full ring-1 ring-black/20 dark:ring-white/15"
              style={current.isGradient
                ? { background: current.bg }
                : { background: current.bg, outline: `2px solid ${current.accent}33` }
              }
            />
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-64 p-3"
        data-testid="theme-swatcher-popover"
      >
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Appearance
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {THEMES.map((t) => (
            <ThemeSwatch
              key={t.id}
              theme={t}
              active={active === t.id}
              onPick={pick}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ThemeSwatch({ theme, active, onPick }) {
  return (
    <button
      data-testid={`theme-swatch-${theme.id}`}
      onClick={() => onPick(theme.id)}
      className={cn(
        'flex flex-col items-center gap-1 rounded-md border p-2 transition-all text-center',
        active
          ? 'border-primary bg-primary/8 shadow-sm'
          : 'border-border bg-card hover:border-primary/40 hover:bg-muted/40',
      )}
    >
      {/* Mini preview tile */}
      <span
        className="h-7 w-full rounded-sm ring-1 ring-black/10 dark:ring-white/10"
        style={theme.isGradient
          ? { background: theme.bg }
          : { background: theme.bg }
        }
      >
        {/* accent stripe */}
        <span
          className="block h-2 w-full rounded-t-sm"
          style={{ background: theme.accent }}
        />
      </span>
      <span className={cn(
        'text-[10.5px] font-medium leading-tight',
        active ? 'text-primary' : 'text-foreground',
      )}>
        {theme.label}
      </span>
    </button>
  );
}

/**
 * Lightweight hook for components that only need to read the current resolved
 * theme (isDark, etc.) — without the full server-sync logic of useTheme.js.
 */
export function useTheme() {
  const [theme, setThemeState] = useState(readStored);

  const setTheme = (next) => {
    setThemeState(next);
    localStorage.setItem('pw-theme', next);
    const resolved = next === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'graphite' : 'light')
      : next;
    applyThemeToDOM(resolved);
  };

  const toggle = () => {
    const next = theme === 'light' ? 'graphite' : 'light';
    setTheme(next);
  };

  return { theme, setTheme, toggle, isDark: theme !== 'light' && theme !== 'system' };
}
